import { Feature, FeatureCollection } from 'geojson';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { LatestScanDict } from '../hooks/useFindLatestScans';
import { getDaysSinceNow } from './getDaysSinceNow';

/**
 * Combines multiple Block geo-polygons into a single GeoJSON FeatureCollection that can be passed to the block outline layer,
 * and appends the latest scan information to each feature/block.
 *
 * @param blocks - An array of Block objects containing geo-polygons.
 * @param latestScans - A dictionary mapping block IDs to their latest scan data.
 * @returns A FeatureCollection containing all blocks with appended scan information, or null if no blocks are provided.
 */
export const addMapBlockInfo = (
  blocks: Block[] | undefined,
  latestScans: LatestScanDict | undefined
): FeatureCollection | null => {
  if (!blocks) return null;

  // Combine all geoJSON features from each block and append blockID.
  // The mapbox layer needs all individual blocks to be combined into a single FeatureCollection,
  // so we extract the features from each block and combine them.
  const blockFeatures: Feature[] = blocks.flatMap((block) => {
    return (
      block.geoPolygon?.features.map((feature) => ({
        ...feature,
        properties: {
          ...feature.properties,
          blockID: block.id,
        },
      })) || []
    );
  });

  // Append scan information to each block feature's properties
  blockFeatures.forEach((feature) => {
    if (latestScans) {
      try {
        const blockID = feature.properties?.blockID;
        const latestScan = latestScans[blockID];

        if (latestScan) {
          feature.properties = {
            ...feature.properties,
            scanDate: latestScan.scanDate,
            scanID: latestScan.scanID,
            daysSinceNow: latestScan.scanDate
              ? getDaysSinceNow(latestScan.scanDate)
              : null,
          };
        }
      } catch (error) {
        console.error(
          'Error adding latest scan info to the block feature: ',
          error
        );
      }
    }
  });

  // Create a single FeatureCollection from all individual block features
  const featureCollection: FeatureCollection = {
    type: 'FeatureCollection',
    features: blockFeatures,
  };

  return featureCollection;
};
